import { useParams, useSearchParams } from 'react-router-dom';
import { SignUp } from '@/components/Views/SignUp/SignUp/SignUp';
import {
  AuthenticationMethodType,
  useGetAuthenticationMethodsQuery,
  useGetInvitationStatusQuery,
} from '@/generated/graphql';
import {
  InlineNotification,
  NotificationType,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { parseDirectInvitationToken } from '@/utils/auth';
import { PageLoader } from '@/components/Elements/PageLoader/PageLoader';
import { useTranslation } from 'react-i18next';
import { useRedirectAppRouter } from '@/hooks/useRedirectAppRouter';
import { useEffect, useState } from 'react';
import { useChangeLocale } from '@/hooks/useChangeLocale';
import { SignupView } from '@/enums/SignupView';
import { EMAIL, REDIRECT_PATH } from '@/constants/authentication';
import { SignUpSSO } from '@/components/Views/SignUp/SignUpSSO/SignUpSSO';
import BackButton from '@/components/Elements/BackButton/BackButton';
import { SignUpSelector } from '@/components/Views/SignUp/SignUpSelector/SignUpSelector';

/**
 * -> DIRECT Invite (token)
 *
 * Direct Invites are being passed with token through URL SearchParam (QueryParams)
 *
 * e.g. (now)
 * https://dev2-candidate.spottedzebra.co.uk/auth/direct-inv?inv=TOKEN
 *
 * should be (future)
 * https://{authAppUrl}/sign-up?direct-inv=TOKEN&redirect_app=company
 *
 */
export function DirectInvite() {
  const changeLocale = useChangeLocale();
  const params = useParams();
  const { t } = useTranslation();
  const router = useRedirectAppRouter();
  const [searchParams] = useSearchParams();
  const { handleMsgType } = useNotification();
  const invitationToken = searchParams.get('direct-inv');
  const projectSubIdFromUrl = searchParams.get('projectSubId');
  const {
    email = '',
    givenName,
    familyName,
    locale,
    projectSubId: projectSubIdFromToken,
  } = parseDirectInvitationToken(invitationToken);
  const [view, setView] = useState<SignupView>(SignupView.EMAIL_PASSWORD);
  const [subId, setSubId] = useState<string>('');

  const projectSubId =
    projectSubIdFromUrl || projectSubIdFromToken || undefined;

  useEffect(() => {
    //Change locale if it is provided inside the direct invite token
    if (locale) {
      changeLocale(locale);
    }
  }, [locale]);

  useEffect(() => {
    // Land the candidate on their role's portal after auth instead of the
    // generic roles list. Persisted the same way the login flow stores
    // redirect_path, so it covers both sign-up completion and the
    // already-has-account bounce to sign-in. Non-portal roles self-correct:
    // the portal route redirects them to the role's stage list.
    if (projectSubId) {
      localStorage.setItem(REDIRECT_PATH, `/roles/${projectSubId}/portal`);
    }
  }, [projectSubId]);

  const { data: invitationData, loading: loadingGetInvitationStatus } =
    useGetInvitationStatusQuery({
      variables: {
        token: invitationToken || '',
      },
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          title: t('common.failed'),
          message: error?.message,
        });
      },
    });

  const { loading: loadingGetAuthMethods, data: dataAuthMethods } =
    useGetAuthenticationMethodsQuery({
      variables: {
        email: email,
      },
      onCompleted: data => {
        const methods = data?.GetAuthenticationMethods || [];

        const hasSSO = methods.some(
          method => method.type === AuthenticationMethodType.Saml
        );
        const hasEmail = methods.some(
          method => method.type === AuthenticationMethodType.EmailPassword
        );
        const hasBoth = hasSSO && hasEmail;
        if (hasBoth) {
          setView(SignupView.SSO_EMAIL_PASSWORD);
        } else if (hasEmail) {
          setView(SignupView.EMAIL_PASSWORD);
        } else if (hasSSO) {
          setView(SignupView.SSO);
        }

        const subId = methods.find(
          method => method.type === AuthenticationMethodType.Saml
        )?.authInfoSubId;

        if (subId) {
          // Save email to localStorage to enable to return to this screen if sso fails
          localStorage.setItem(EMAIL, email);
          setSubId(subId);
        }
      },
    });

  useEffect(() => {
    if (invitationData?.getInvitationStatus.isCompleted) {
      router.redirectToLoginWithEmail(email);
    }
  }, [invitationData]);

  if (invitationData?.getInvitationStatus.isExpired) {
    return (
      <InlineNotification
        notificationType={NotificationType.ERROR}
        title={t('signUp.invitationExpired')}
      >
        <p>{t('signUp.weWouldLoveToCreateAccount')}</p>
        <p>{t('signUp.pleaseReachOutToYourContact')}</p>
      </InlineNotification>
    );
  }

  const isLoading = loadingGetAuthMethods || loadingGetInvitationStatus;

  const hasBackBtn =
    (dataAuthMethods?.GetAuthenticationMethods.length || 0) > 1;

  const renderView = () => {
    if (view === SignupView.EMAIL_PASSWORD)
      return (
        <>
          {hasBackBtn && (
            <BackButton
              onClick={() => setView(SignupView.SSO_EMAIL_PASSWORD)}
              text={t('signUp.signUpMethod')}
            />
          )}
          <SignUp
            email={email}
            givenName={givenName}
            familyName={familyName}
            autofillAndLock
            invitationToken={invitationToken || undefined}
            locale={locale || params.lang || 'en'}
            projectSubId={projectSubId}
          />
        </>
      );

    if (view === SignupView.SSO)
      return (
        <>
          {hasBackBtn && (
            <BackButton
              onClick={() => setView(SignupView.SSO_EMAIL_PASSWORD)}
              text={t('signUp.signUpMethod')}
            />
          )}
          <SignUpSSO
            email={email}
            givenName={givenName}
            familyName={familyName}
            subId={subId}
          />
        </>
      );

    if (view === SignupView.SSO_EMAIL_PASSWORD)
      return <SignUpSelector email={email} setView={setView} />;
  };

  return <>{isLoading ? <PageLoader /> : renderView()}</>;
}
